import debounce from 'lodash.debounce';
import { isDesktop } from 'Utils/viewport';
import { getScrollPosition, requireElement } from './dom';
import type { BrandAlertCreativeData, DismissType, HoverData, PopupDelegate, PopupState } from './types';
import { DISMISS_TYPE, STATE } from './types';

class HoverTracker {
    private _timeEnter: number | null = null;
    private _maxY: number | null = null;

    constructor(
        private readonly _element: HTMLElement,
        private readonly _callback: (data: HoverData) => void,
        private readonly _signal: AbortSignal,
    ) {
        this._track();
    }

    finalize(): void {
        if (this._timeEnter) {
            this._callback({ duration: Date.now() - this._timeEnter, maxY: this._maxY });
            this._reset();
        }
    }

    private _reset(): void {
        this._timeEnter = null;
        this._maxY = null;
    }

    private _track(): void {
        const signal = this._signal;
        this._element.addEventListener(
            'mouseenter',
            () => {
                this._timeEnter = Date.now();
            },
            { signal },
        );
        this._element.addEventListener(
            'mousemove',
            (e) => {
                this._maxY = Math.max(
                    this._maxY ?? 0,
                    e.clientY + this._element.scrollTop - this._element.getBoundingClientRect().top,
                );
            },
            { signal },
        );
        this._element.addEventListener('mouseleave', () => this.finalize(), { signal });
    }
}

/**
 * One rendered sideguide: its DOM, its interaction tracking, and its own
 * dismiss/rotation countdown.
 *
 * Talks to its owner only through {@link PopupDelegate}.
 */
export class BrandAlertPopup {
    readonly root: HTMLElement;
    readonly container: HTMLElement;
    readonly secondaryWrap: HTMLElement;
    readonly viewedId: number;
    readonly placementId: number | null;

    /** GAM creative id once the banner fills; sent with tracking. */
    bannerCreativeId: string | null = null;
    /** Set by the client when this alert embeds a banner ad. */
    sideguideContainerId: string | null = null;

    private _state: PopupState = STATE.START;
    private _shownTime: number | null = null;
    /** Guards against tracking a second dismiss for the same alert. */
    private _finalized = false;
    private _destroyed = false;
    /** Detaches every listener `_setUpEvents` attaches. Without this a second
     * client over the same shell doubles each popup handler — one close click
     * would dismiss both popups and POST twice. */
    private readonly _listeners = new AbortController();
    private _dismissTimeSeconds: number;
    private _showNextTimeout: ReturnType<typeof setTimeout> | null = null;
    private _hoverTrackers: HoverTracker[] = [];

    private readonly _header: HTMLElement;
    private readonly _headerLinks: HTMLElement;
    /** Can be non-present on iOS (`render_handlebar=False`). */
    private readonly _handleBar: HTMLElement | null;
    private readonly _innerWrap: HTMLElement;
    private readonly _content: HTMLElement;
    private readonly _info: HTMLElement;
    private readonly _infoHeader: HTMLElement;
    private readonly _infoTitle: HTMLElement;
    private readonly _infoContent: HTMLElement;
    private readonly _indications: HTMLElement;
    private readonly _indicationsHeader: HTMLElement;
    private readonly _indicationsTitle: HTMLElement;
    private readonly _indicationsContent: HTMLElement;

    constructor(
        root: HTMLElement,
        data: BrandAlertCreativeData,
        private readonly _delegate: PopupDelegate,
    ) {
        this.root = root;
        // Scoped to this popup's own ancestor rather than the document: with two
        // surfaces live on one page, a document-wide lookup would hand both popups
        // the first container.
        const container = root.closest<HTMLElement>('.js_popupContainer');
        if (!container) {
            throw new Error('brand-alerts: popup is not inside a .js_popupContainer');
        }
        this.container = container;
        this._header = requireElement(root, '.js_popupHeader');
        this._headerLinks = requireElement(root, '.js_headerLinks');
        this._handleBar = root.querySelector('.js_popupHandleBar');
        this._innerWrap = requireElement(root, '.js_innerWrap');
        this._content = requireElement(root, '.js_content');
        this.secondaryWrap = requireElement(root, '.js_secondaryWrap');

        this._info = requireElement(root, '.js_info');
        this._infoHeader = requireElement(this._info, '.js_infoHeader');
        this._infoTitle = requireElement(this._info, '.js_isiTitle');
        this._infoContent = requireElement(root, '.js_infoContent');

        this._indications = requireElement(root, '.js_indications');
        this._indicationsHeader = requireElement(this._indications, '.js_indicationsHeader');
        this._indicationsTitle = requireElement(this._indications, '.js_indicationsTitle');
        this._indicationsContent = requireElement(this._indications, '.js_indicationsContent');

        this._dismissTimeSeconds = data.dismiss_time_seconds;
        this.viewedId = data.brand_alert_viewed_id;
        this.placementId = data.brand_alert_placement_id;

        this._populateData(data);
        this._setUpEvents();
        this.trackEvent({ eventName: 'alert_loaded' });
    }

    /**
     * Methods rather than a narrowed `state` comparison: the client re-checks
     * these across an `await`, and TypeScript would otherwise carry the earlier
     * narrowing over the suspension point and call the second check dead.
     */
    isClosed(): boolean {
        return this._state === STATE.CLOSED;
    }

    isPending(): boolean {
        return this._state === STATE.START;
    }

    get shown(): boolean {
        return this._shownTime !== null;
    }

    trackEvent(event: { eventName: string; data?: Record<string, unknown> }): void {
        // A closed display tracks nothing. `markClosed()` drops the listeners, but
        // the scroll handlers are debounced, so one trailing call can still land up
        // to 300ms after the close — this is what stops it.
        if (this._destroyed || this.isClosed()) {
            return;
        }

        // `bannerCreative` is a top-level field the tracking serializer expects. Set when the banner
        // fills, so it rides expanded_shown and every later call; omitted when the sideguide shows
        // without a fill, and on alert_loaded (fired before the fill).
        this._delegate.track({
            brandAlertViewedId: this.viewedId,
            ...event,
            ...(this.bannerCreativeId != null ? { bannerCreative: this.bannerCreativeId } : {}),
        });
    }

    expand(): void {
        this._delegate.beforeExpand(this);

        this.container.classList.add('is-half-expanded');
        this._state = STATE.HALF_EXPANDED;
        this.container.style.transform = 'translateY(0)';

        this._shownTime = Date.now();

        this.trackEvent({ eventName: 'expanded_shown' });
        this.resetShowNextTimeout();
        this.updateHeaderRows();
    }

    /**
     * Promote an incoming alert that has just slid into place.
     */
    promote(): void {
        this._state = STATE.HALF_EXPANDED;
        this.trackEvent({ eventName: 'expanded_shown' });
        this.resetShowNextTimeout();
        this._shownTime = Date.now();
        this.updateHeaderRows();
    }

    updateHeaderRows(): void {
        // Header link rows drive expanded-view styling; 18 = one link row's height in px.
        this.root.dataset.headerRows = String(this._headerLinks.clientHeight / 18);
    }

    getExpandedDurationData(): Record<string, unknown> {
        return this._shownTime ? { expandedDuration: Date.now() - this._shownTime } : {};
    }

    finalizeAlert(dismissType: DismissType, dismissData?: Record<string, unknown>): void {
        // Finalize once. During the post-fill swap the outgoing alert is finalized (auto_dismiss) but
        // stays active for ~1.7s; a manual close in that window would otherwise track a second dismiss.
        if (this._finalized) return;
        this._finalized = true;

        let data = this.getExpandedDurationData();
        if (dismissData) {
            data = { ...dismissData, ...data };
        }

        // Cancel the move-to-next countdown so a re-armed timeout can't fire a stray rotation after
        // this alert is finalized (on dismiss or when it transitions out).
        if (this._showNextTimeout) clearTimeout(this._showNextTimeout);
        this._hoverTrackers.forEach((tracker) => tracker.finalize());
        this.trackEvent({ eventName: dismissType, data });
    }

    dismiss(dismissType: DismissType, data?: Record<string, unknown>): void {
        if (this._state === STATE.CLOSED) return;

        // Track the dismiss while still open, then close: `trackEvent` is inert
        // once the state flips.
        this.finalizeAlert(dismissType, data);
        // Mark closed before handing off so it is platform-independent — the iOS close path can't set
        // it, and the rotation guards rely on it on both platforms.
        this.markClosed();
        this._delegate.onDismissed(this, dismissType);
    }

    /**
     * Mark this popup closed without tracking or notifying the owner.
     *
     * Used directly when the *other* popup in a rotation ended the display: the
     * one left behind must not look dismissable, or a later page-close or
     * fullscreen signal would dismiss it again and fire a second
     * `brand-alert:dismissed` carrying a type that never happened.
     *
     * Drops the DOM listeners as well. A closed sideguide has no interactive
     * surface, but its nodes stay in the shared shell, so handlers left attached
     * keep tracking clicks, toggles and scrolls against a display that is over.
     */
    markClosed(): void {
        this._state = STATE.CLOSED;
        if (this._showNextTimeout) clearTimeout(this._showNextTimeout);
        this._showNextTimeout = null;
        this._listeners.abort();
    }

    /**
     * Stop the dismiss countdown. The client calls this on teardown so a
     * destroyed client cannot still fire a rotation.
     */
    destroy(): void {
        this._destroyed = true;
        if (this._showNextTimeout) clearTimeout(this._showNextTimeout);
        this._showNextTimeout = null;
        this._listeners.abort();
    }

    resetShowNextTimeout(): void {
        if (this._destroyed) return;
        // A finalized alert (transitioned out or dismissed) must never re-arm the move-to-next
        // countdown — otherwise interaction during the swap could advance the incoming alert early.
        if (this._finalized) return;

        if (this._showNextTimeout) clearTimeout(this._showNextTimeout);
        this._showNextTimeout = setTimeout(
            () => this._delegate.onDismissTimerElapsed(this),
            this._dismissTimeSeconds * 1000,
        );
    }

    private _populateData(data: BrandAlertCreativeData): void {
        this.root.classList.toggle('brand-alert-popup--double-isi', !!data.double_isi);

        this.root.classList.toggle('brand-alert-popup--partial-indication', !!data.partial_indication_enabled);
        if (data.partial_indication_enabled) {
            this.root.style.setProperty('--partial-indication-height', `${data.partial_indication_height}px`);
        }

        this.root.classList.toggle('brand-alert-popup--wysiwyg-editor', !!data.wysiwyg_editor);

        // Hide the Indications / ISI trays when the creative opts out (defaults to shown, so only an
        // explicit false hides). Both hidden collapses the secondary wrap — e.g. a banner-only creative.
        this.root.classList.toggle('brand-alert-popup--no-indications', data.show_indications === false);
        this.root.classList.toggle('brand-alert-popup--no-isi', data.show_isi === false);

        this._headerLinks.innerHTML = data.header;
        // Prepend because there's a dismiss button
        requireElement(this.root, '.js_headerContentWrap').innerHTML = data.headline;
        this._content.innerHTML = data.expanded;
        this._indicationsContent.innerHTML = data.indications;
        this._infoContent.innerHTML = data.isi;
        this._indicationsTitle.innerHTML = data.indications_title;
        this._infoTitle.innerHTML = data.isi_title;
    }

    private _setUpEvents(): void {
        const { signal } = this._listeners;

        this._hoverTrackers = [
            // Expanded hover
            new HoverTracker(
                this._innerWrap,
                (data) => {
                    if (this._shownTime) this.trackEvent({ eventName: 'expanded_hover', data });
                },
                signal,
            ),
            // ISI hover
            new HoverTracker(this._infoContent, (data) => this.trackEvent({ eventName: 'isi_hover', data }), signal),
            // Indications hover
            new HoverTracker(
                this._indications,
                (data) => this.trackEvent({ eventName: 'indications_hover', data }),
                signal,
            ),
        ];

        // Tapping beside the sheet dismisses it.
        document.addEventListener('click', (e) => this._onDocumentClick(e), { capture: true, signal });

        // block page scrolling when touching the handlebar / tray headers
        this._handleBar?.addEventListener('touchmove', (e) => e.preventDefault(), { signal });
        this._indicationsHeader.addEventListener('touchmove', (e) => e.preventDefault(), { signal });
        this._infoHeader.addEventListener('touchmove', (e) => e.preventDefault(), { signal });

        // handleBar click-to-expand
        this._handleBar?.addEventListener('click', () => this._handleBarClick(), { signal });

        // indications headline click
        this._indicationsHeader.addEventListener('click', () => this._toggleIndicationsInformation(), { signal });

        // safety information headline click
        this._infoHeader.addEventListener('click', () => this._toggleSafetyInformation(), { signal });

        // Manual close
        requireElement(this.root, '.js_popupCloseBtn').addEventListener(
            'click',
            () => this.dismiss(DISMISS_TYPE.MANUAL_DISMISS),
            { signal },
        );

        // Expanded scroll
        this._handleScroll(this._innerWrap, 'expanded_scroll');

        // URL click
        Array.from(this.root.querySelectorAll('a')).forEach((linkItem) => {
            linkItem.addEventListener(
                'click',
                () => {
                    const data = { text: linkItem.textContent, url: linkItem.href };
                    this.trackEvent({ eventName: 'url_click', data });
                    return false;
                },
                { signal },
            );
        });

        // Expanded headline click
        this._header.addEventListener(
            'click',
            () => {
                if (this._state !== STATE.CLOSED) {
                    this.trackEvent({ eventName: 'expanded_headline_click', data: getScrollPosition(this._innerWrap) });
                    this.resetShowNextTimeout();
                }
            },
            { signal },
        );

        // Expanded body click
        this._content.addEventListener(
            'click',
            () => {
                if (this._state !== STATE.CLOSED) {
                    this.trackEvent({ eventName: 'expanded_click', data: getScrollPosition(this._innerWrap) });
                    this.resetShowNextTimeout();
                }
            },
            { signal },
        );

        // Indications click
        this._indications.addEventListener(
            'click',
            () => {
                this.trackEvent({ eventName: 'indications_click', data: getScrollPosition(this._indications) });
                this.resetShowNextTimeout();
            },
            { signal },
        );

        // Indications scroll
        this._handleScroll(this._indicationsContent, 'indications_scroll');

        // ISI scroll
        this._handleScroll(this._infoContent, 'isi_scroll');

        // ISI click
        this._info.addEventListener(
            'click',
            () => {
                this.trackEvent({ eventName: 'isi_click', data: getScrollPosition(this._info) });
                this.resetShowNextTimeout();
            },
            { signal },
        );
    }

    private _toggleSafetyInformation(): void {
        this.secondaryWrap.classList.toggle('safety-expanded');
        this.secondaryWrap.classList.remove('indications-expanded');
    }

    private _toggleIndicationsInformation(): void {
        this.secondaryWrap.classList.toggle('indications-expanded');
        this.secondaryWrap.classList.remove('safety-expanded');
    }

    private _handleBarClick(): void {
        // Whichever is smaller: innerHeight counts the horizontal scrollbar, clientHeight counts
        // the area behind a mobile browser's toolbars. Either overshoot makes the container taller
        // than the fixed viewport it hangs off, pushing the drag handle out of sight.
        const windowHeight = Math.min(window.innerHeight, document.documentElement.clientHeight);
        const video = requireElement<HTMLElement>(document, '.js_videoTab video');
        const videoHeight = video.getBoundingClientRect().height;

        if (this._state === STATE.FULL_EXPANDED) {
            this.container.style.height = `calc(100svh - ${videoHeight}px)`;
            this.container.classList.remove('is-full-expanded');
            this._state = STATE.HALF_EXPANDED;
            this.trackEvent({ eventName: 'handlebar_collapsed' });
        } else {
            this.container.classList.add('is-full-expanded');
            this._state = STATE.FULL_EXPANDED;
            this.container.style.height = `${windowHeight}px`;
            this.trackEvent({ eventName: 'handlebar_expanded' });
        }
    }

    /**
     * Dismiss on a tap in the strip beside the sheet.
     */
    private _onDocumentClick(event: MouseEvent): void {
        if (!this.shown || this.isClosed()) {
            return;
        }
        if (isDesktop()) {
            return;
        }

        const { top, bottom, left, right } = this.container.getBoundingClientRect();
        const withinSheetRow = event.clientY >= top && event.clientY <= bottom;
        const onTheSheet = event.clientX >= left && event.clientX <= right;
        if (!withinSheetRow || onTheSheet) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.dismiss(DISMISS_TYPE.MANUAL_DISMISS);
    }

    private _handleScroll(element: HTMLElement, eventName: string): void {
        const { signal } = this._listeners;
        // Source: https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#determine_if_an_element_has_been_totally_scrolled
        const checkScrollToBottom = () =>
            Math.abs(element.scrollHeight - element.clientHeight - element.scrollTop) <= 1;

        let scrolledToBottom = false;
        element.addEventListener(
            'scroll',
            debounce(() => {
                let trackingEventName = eventName;
                if (!scrolledToBottom && checkScrollToBottom()) {
                    trackingEventName += '_bottom';
                    scrolledToBottom = true;
                }

                this.trackEvent({ eventName: trackingEventName, data: getScrollPosition(element) });
                this.resetShowNextTimeout();
            }, 300),
            { signal },
        );
    }
}
